import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, extractErrorMessage } from '../api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [stores, setStores] = useState([]);
  const [storesTotal, setStoresTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [expandedStore, setExpandedStore] = useState(null);
  const [storeDetail, setStoreDetail] = useState(null);
  const [overrideForm, setOverrideForm] = useState({ tier: '', custom_limits: '', notes: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  // Redirect non-admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await adminAPI.getDashboard();
      setDashboard(res.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load dashboard'));
    }
  }, []);

  const loadStores = useCallback(async () => {
    try {
      const params = { page, page_size: 20 };
      if (search) params.search = search;
      if (filterTier) params.tier = filterTier;
      if (filterStatus) params.sub_status = filterStatus;
      const res = await adminAPI.listStores(params);
      setStores(res.data.stores || []);
      setStoresTotal(res.data.total || 0);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load stores'));
    }
  }, [page, search, filterTier, filterStatus]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadDashboard(), loadStores()]);
      setLoading(false);
    };
    init();
  }, [loadDashboard, loadStores]);

  const handleExpandStore = async (storeId) => {
    if (expandedStore === storeId) {
      setExpandedStore(null);
      setStoreDetail(null);
      return;
    }
    try {
      const res = await adminAPI.getStore(storeId);
      setStoreDetail(res.data);
      setExpandedStore(storeId);
      const sub = res.data.subscription;
      setOverrideForm({
        tier: sub?.tier || 'free',
        custom_limits: sub?.custom_limits ? JSON.stringify(sub.custom_limits) : '',
        notes: '',
      });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleOverride = async (storeId) => {
    try {
      setActionLoading(true);
      const data = { tier: overrideForm.tier, notes: overrideForm.notes };
      if (overrideForm.custom_limits) {
        data.custom_limits = JSON.parse(overrideForm.custom_limits);
      }
      await adminAPI.overrideSubscription(storeId, data);
      setActionMsg('Subscription updated');
      setTimeout(() => setActionMsg(null), 3000);
      loadStores();
    } catch (err) {
      setError(extractErrorMessage(err, 'Override failed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async (storeId) => {
    if (!window.confirm('Suspend this store?')) return;
    try {
      setActionLoading(true);
      await adminAPI.suspendStore(storeId);
      setActionMsg('Store suspended');
      setTimeout(() => setActionMsg(null), 3000);
      loadStores();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (storeId) => {
    try {
      setActionLoading(true);
      await adminAPI.reactivateStore(storeId);
      setActionMsg('Store reactivated');
      setTimeout(() => setActionMsg(null), 3000);
      loadStores();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="loading"><div className="spinner"></div><span>Loading...</span></div>;

  return (
    <div className="admin-dashboard-page" style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div className="page-header">
        <h1 className="page-title">Admin Dashboard</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Cross-tenant management</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {actionMsg && <div className="alert alert-success">{actionMsg}</div>}

      {/* KPI Row */}
      {dashboard && (
        <div className="kpi-grid" style={{ marginBottom: '2rem' }}>
          <div className="kpi-card primary">
            <div className="kpi-label">Total Stores</div>
            <div className="kpi-value">{dashboard.total_stores}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Total Users</div>
            <div className="kpi-value">{dashboard.total_users}</div>
          </div>
          <div className="kpi-card success">
            <div className="kpi-label">Monthly Revenue</div>
            <div className="kpi-value">${dashboard.total_monthly_revenue?.toFixed(2)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Active Subscriptions</div>
            <div className="kpi-value">{dashboard.active_subscriptions}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">New Stores (Month)</div>
            <div className="kpi-value">{dashboard.new_stores_this_month}</div>
          </div>
          {dashboard.subscriptions_by_tier && Object.entries(dashboard.subscriptions_by_tier).map(([tier, count]) => (
            <div className="kpi-card" key={tier}>
              <div className="kpi-label">{tier.charAt(0).toUpperCase() + tier.slice(1)}</div>
              <div className="kpi-value">{count}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body">
          <div className="filter-bar admin-dashboard-filters">
            <input
              type="text"
              className="form-input admin-dashboard-filter-input"
              placeholder="Search stores..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{ maxWidth: '300px' }}
            />
            <select className="form-input admin-dashboard-filter-input" value={filterTier} onChange={(e) => { setFilterTier(e.target.value); setPage(1); }} style={{ maxWidth: '160px' }}>
              <option value="">All Tiers</option>
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="ai_pro">AI Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
            <select className="form-input admin-dashboard-filter-input" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} style={{ maxWidth: '160px' }}>
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="trialing">Trial</option>
              <option value="expired">Expired</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Store Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Stores ({storesTotal})</h3>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Owner</th>
                <th>Tier</th>
                <th>Status</th>
                <th>Products</th>
                <th>Staff</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {stores.map(store => (
                <React.Fragment key={store.id}>
                  <tr onClick={() => handleExpandStore(store.id)} style={{ cursor: 'pointer' }}>
                    <td className="font-bold">{store.name}</td>
                    <td>{store.owner_email}</td>
                    <td><span className="badge badge-primary">{store.tier}</span></td>
                    <td>
                      <span className={`badge ${store.status === 'active' || store.status === 'trialing' ? 'badge-success' : store.status === 'expired' ? 'badge-danger' : 'badge-warning'}`}>
                        {store.status}
                      </span>
                    </td>
                    <td>{store.product_count}</td>
                    <td>{store.staff_count}</td>
                    <td>{store.created_at ? new Date(store.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                  {expandedStore === store.id && storeDetail && (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--gray-50)', padding: '1.5rem' }}>
                        <div className="admin-dashboard-detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                          <div>
                            <h4 style={{ marginBottom: '0.75rem', fontSize: '0.85rem', fontWeight: 600 }}>Override Subscription</h4>
                            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                              <label className="form-label">Tier</label>
                              <select className="form-input" value={overrideForm.tier} onChange={(e) => setOverrideForm({ ...overrideForm, tier: e.target.value })}>
                                <option value="free">Free</option>
                                <option value="starter">Starter</option>
                                <option value="growth">Growth</option>
                                <option value="ai_pro">AI Pro</option>
                                <option value="enterprise">Enterprise</option>
                              </select>
                            </div>
                            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                              <label className="form-label">Custom Limits (JSON)</label>
                              <input type="text" className="form-input" placeholder='{"max_products": 100}' value={overrideForm.custom_limits} onChange={(e) => setOverrideForm({ ...overrideForm, custom_limits: e.target.value })} />
                            </div>
                            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                              <label className="form-label">Notes</label>
                              <input type="text" className="form-input" value={overrideForm.notes} onChange={(e) => setOverrideForm({ ...overrideForm, notes: e.target.value })} />
                            </div>
                            <button className="btn btn-primary btn-sm" onClick={() => handleOverride(store.id)} disabled={actionLoading}>
                              Save Override
                            </button>
                          </div>
                          <div>
                            <h4 style={{ marginBottom: '0.75rem', fontSize: '0.85rem', fontWeight: 600 }}>Actions</h4>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              {storeDetail.subscription?.status !== 'expired' ? (
                                <button className="btn btn-danger btn-sm" onClick={() => handleSuspend(store.id)} disabled={actionLoading}>
                                  Suspend Store
                                </button>
                              ) : (
                                <button className="btn btn-success btn-sm" onClick={() => handleReactivate(store.id)} disabled={actionLoading}>
                                  Reactivate Store
                                </button>
                              )}
                            </div>
                            <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                              <div>Type: {storeDetail.store_type}</div>
                              <div>Owner: {storeDetail.owner_name} ({storeDetail.owner_email})</div>
                              <div>Monthly Sales: {storeDetail.subscription?.current_month_sales || 0}</div>
                              <div>Monthly Price: ${storeDetail.subscription?.monthly_price || 0}</div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {stores.length === 0 && (
                <tr><td colSpan={7} className="text-center text-muted" style={{ padding: '2rem' }}>No stores found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {storesTotal > 20 && (
          <div className="pagination admin-dashboard-pagination" style={{ padding: '1rem' }}>
            <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
            <span className="admin-dashboard-page-label" style={{ margin: '0 1rem', fontSize: '0.85rem' }}>Page {page}</span>
            <button className="btn btn-sm btn-secondary" disabled={stores.length < 20} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
